import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@Sentry";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation, type TranslatedString } from "@i18n";
import { showToastNotification } from "@Reducers/toastSlice";
import { setPortalChatUnreadCount } from "@Reducers/portalChat";
import {
  getConversationsRequest,
  getMessagesRequest,
  sendMessageRequest,
  editMessageRequest,
  deleteMessageRequest,
  toggleReactionRequest,
  markConversationReadRequest,
  updateConversationStateRequest,
  deleteAttachmentRequest,
  getUnreadSummaryRequest,
  type ChatConversation,
  type ChatSection,
  type ChatMessage,
} from "../api/portalChat.api";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChatSocket } from "../hooks/usePortalChatSocket";
import {
  SCOPE_STORAGE_KEY,
  LAST_CONVERSATION_STORAGE_KEY,
  TYPING_TTL_MS,
  TYPING_SWEEP_INTERVAL_MS,
  BADGE_REFRESH_DEBOUNCE_MS,
  PREVIEW_LENGTH,
  UNREAD_COUNT_CAP,
  THREAD_WINDOW_SIZE,
  translateServerError,
} from "../portalChat.constants";
import type { PortalChatContextValue, ThreadData, ComposerIntent, ChatScope, FailedSend } from "../components/context";
import type { PortalChatPlatform } from "./platform";

/**
 * The whole Portal Chat brain, shared verbatim by the web panel (PortalChatPanel) and the mobile
 * screens (packages/mobile/src/Components/PortalChat) — list state, open thread, socket handlers,
 * unread badge, resurface rules, typing TTLs, reconnect resync. Everything platform-specific is
 * behind the `PortalChatPlatform` adapter; the platform shells only render.
 *
 * Redux is shared too: the toast slice and the portalChat badge slice are registered in BOTH
 * stores (web reducers/index.ts, mobile rootReducer.ts), so the core dispatches them directly.
 */
export function usePortalChatCore({
  show,
  userId,
  userName,
  currentProjectId,
  platform,
  sessionKey,
}: {
  /** Web: the panel is open. Mobile: a chat screen is focused. Gates list upkeep vs badge-only. */
  show: boolean;
  userId: string;
  userName: string;
  /** The project the user is working in right now (web route / mobile param), or null. */
  currentProjectId: string | null;
  platform: PortalChatPlatform;
  /**
   * Identity of the chat session (mobile: portal id). Changing it resets EVERY piece of chat
   * state and reconnects the socket with fresh credentials — without unmounting anything, so
   * navigation state survives a portal switch. Web omits it (portal switches are full page loads).
   */
  sessionKey?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [scope, setScopeState] = useState<ChatScope>(() => {
    // PRD §4.7: the last picked scope persists per tab/device (the open-effect below overrides to
    // Project Chats when chat is opened from inside a project).
    const stored = platform.storage.getItem(SCOPE_STORAGE_KEY);
    if (stored === "all" || stored === "direct" || stored === "group" || stored === "project") return stored;
    return "all";
  });
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [sections, setSections] = useState<ChatSection[]>([]);
  const [projectOrder, setProjectOrder] = useState<string[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  /** True once a conversations list for THIS portal session has arrived (reset on portal switch). */
  const [listLoaded, setListLoaded] = useState(false);
  const [connected, setConnected] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadData | null>(null);
  const [composerIntent, setComposerIntent] = useState<ComposerIntent>(null);
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const [typingByConversation, setTypingByConversation] = useState<Record<string, Record<string, { until: number }>>>(
    {},
  );
  const [forwardMessage, setForwardMessage] = useState<ChatMessage | null>(null);
  const [receiptsMessage, setReceiptsMessage] = useState<ChatMessage | null>(null);
  const [reactionsMessage, setReactionsMessage] = useState<ChatMessage | null>(null);
  const [editDialogMessage, setEditDialogMessage] = useState<ChatMessage | null>(null);
  const [failedSend, setFailedSend] = useState<FailedSend | null>(null);
  const [menuEpoch, setMenuEpoch] = useState(0);

  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  const threadRef = useRef<ThreadData | null>(null);
  threadRef.current = thread;
  const showRef = useRef(show);
  showRef.current = show;
  const summaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every sessionKey (portal) switch; long-flight responses from the previous portal
  // compare against it and discard themselves instead of repopulating cleared state.
  const sessionEpochRef = useRef(0);
  const prevConnectedRef = useRef(true);
  const platformRef = useRef(platform);
  platformRef.current = platform;
  const currentProjectIdRef = useRef(currentProjectId);
  currentProjectIdRef.current = currentProjectId;

  const showToast = useCallback(
    (content: TranslatedString, success = true) => {
      dispatch(showToastNotification({ content, type: success ? "success" : "error" }));
    },
    [dispatch],
  );

  /** Server errors arrive as fixed English sentences — map them through i18n before toasting. */
  const showServerError = useCallback(
    (serverMessage: string | undefined, fallback: TranslatedString) =>
      showToast(translateServerError(t, serverMessage, fallback), false),
    [showToast, t],
  );

  const publishBadge = useCallback(
    (rows: ChatConversation[]) => {
      // Muted AND hidden conversations are excluded from the icon badge (PRD §4.5/§4.11).
      const count = rows.filter((c) => !c.muted && !c.hidden && c.unreadCount > 0).length;
      dispatch(setPortalChatUnreadCount(count));
    },
    [dispatch],
  );

  const refreshList = useCallback(async () => {
    const epoch = sessionEpochRef.current;
    try {
      const response = await getConversationsRequest();
      if (epoch !== sessionEpochRef.current) return;
      if (response.data.status) {
        const nowMs = Date.now();
        for (const [id, at] of recentlyReadRef.current) {
          if (nowMs - at > 15000) recentlyReadRef.current.delete(id);
        }
        const rows = (response.data.result ?? []).map((c) =>
          recentlyReadRef.current.has(c._id) ? { ...c, unreadCount: 0 } : c,
        );
        setConversations(rows);
        setSections(response.data.sections ?? []);
        setProjectOrder((response.data.projectOrder ?? []).map(String));
        setListLoaded(true);
        publishBadge(rows);
      }
    } catch (error) {
      Sentry.captureException(error);
      showToast(t("Could not load chats"), false);
    }
  }, [publishBadge, showToast, t]);

  /** Conversations marked read very recently — a refreshList whose server snapshot predates the
   *  mark must not resurrect their unread counts. */
  const recentlyReadRef = useRef(new Map<string, number>());

  const markRead = useCallback(
    (conversationId: string) => {
      recentlyReadRef.current.set(conversationId, Date.now());
      void markConversationReadRequest({ conversationId: toObjectIdString(conversationId) }).catch((error: unknown) =>
        Sentry.captureException(error),
      );
      setConversations((prev) => {
        const next = prev.map((c) =>
          c._id === conversationId ? { ...c, unreadCount: 0, lastReadDate: Date.now() } : c,
        );
        publishBadge(next);
        return next;
      });
    },
    [publishBadge],
  );

  // A message that arrives while the thread is open but NOT visible (hidden tab / backgrounded
  // app) is appended without consuming a read receipt; the read catches up the moment the thread
  // is actually seen again, so senders get their double tick and no phantom badge resurrects.
  const pendingReadRef = useRef<string | null>(null);
  useEffect(() => {
    const subscribe = platformRef.current.subscribeAppVisibility;
    if (!subscribe) return undefined;
    return subscribe(() => {
      const pending = pendingReadRef.current;
      if (pending && pending === selectedIdRef.current && platformRef.current.isAppVisible()) {
        pendingReadRef.current = null;
        markRead(pending);
      }
    });
  }, [markRead]);

  /** Monotonic per-select sequence: a slow response from an OLDER select of the SAME conversation
   *  must not clobber a newer one (the id-equality guard alone cannot see that). */
  const selectSeqRef = useRef(0);
  /** The last non-null selection — an armed reply intent survives transient deselects and
   *  re-selects of the SAME conversation (mobile camera round-trip), clears on real switches. */
  const lastSelectedRef = useRef<string | null>(null);

  const selectConversation = useCallback(
    (conversationId: string | null) => {
      pendingReadRef.current = null;
      selectSeqRef.current += 1;
      const seq = selectSeqRef.current;
      const isNewConversation = conversationId !== null && conversationId !== lastSelectedRef.current;
      if (conversationId !== null) lastSelectedRef.current = conversationId;
      setSelectedId(conversationId);
      if (isNewConversation) setComposerIntent(null);
      setHighlightMessageId(null);
      if (conversationId) platformRef.current.storage.setItem(LAST_CONVERSATION_STORAGE_KEY, conversationId);
      if (!conversationId) {
        setThread(null);
        return;
      }
      setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: true });
      void getMessagesRequest({ conversationId: toObjectIdString(conversationId) })
        .then((response) => {
          if (selectedIdRef.current !== conversationId || seq !== selectSeqRef.current) return;
          if (response.data.status) {
            const fetched = response.data.result ?? [];
            const memberReadDates = (response.data.memberReadDates ?? []).map((r) => ({
              userId: String(r.userId),
              lastReadDate: r.lastReadDate,
            }));
            const members = response.data.members ?? [];
            const hasMore = response.data.hasMore ?? false;
            // Merge, don't replace: a socket message that landed while this fetch was in flight
            // was already appended to the placeholder thread and may be NEWER than the response's
            // window — a wholesale replace would silently drop it until the next re-select.
            setThread((prev) => {
              const fetchedIds = new Set(fetched.map((m) => m._id));
              const newest = fetched.at(-1)?.createdDate ?? 0;
              const liveTail = (prev?.messages ?? []).filter((m) => !fetchedIds.has(m._id) && m.createdDate >= newest);
              return { messages: [...fetched, ...liveTail], members, memberReadDates, hasMore, loading: false };
            });
            // Same visibility rule as live messages: a reconnect-driven refetch in a hidden
            // tab/backgrounded app must not consume read receipts; the subscription catches up.
            if (platformRef.current.isAppVisible()) markRead(conversationId);
            else pendingReadRef.current = conversationId;
          } else {
            setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: false, error: true });
          }
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
          if (selectedIdRef.current === conversationId && seq === selectSeqRef.current) {
            setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: false, error: true });
          }
        });
    },
    [markRead],
  );

  // Open/close transitions (placed after selectConversation so the deps don't hit its TDZ).
  useEffect(() => {
    if (!show) return;
    setLoadingList(true);
    void refreshList().finally(() => setLoadingList(false));
    // PRD §4.7: opening from inside a project always lands on Project Chats, overriding the last
    // scope — but WITHOUT overwriting the persisted preference (setScopeState, not setScope).
    if (platformRef.current.forceProjectScopeOnOpen && currentProjectIdRef.current) setScopeState("project");
    // A selection retained from before chat was closed may be stale — messages that arrived
    // while closed were never appended (closed-chat events only refresh the badge). Refetch it.
    if (selectedIdRef.current) selectConversation(selectedIdRef.current);
  }, [show, refreshList, selectConversation]);

  // PRD §4.1: the Chats icon opens the last conversation, or the empty state if none exists.
  // Mobile opts out (restoreLastConversationOnOpen: false): its navigation stack restores itself,
  // and a background auto-select would consume read receipts for messages the user never saw.
  useEffect(() => {
    if (!show || selectedIdRef.current || !platformRef.current.restoreLastConversationOnOpen) return;
    const lastId = platformRef.current.storage.getItem(LAST_CONVERSATION_STORAGE_KEY);
    if (lastId && conversations.some((c) => c._id === lastId && !c.hidden)) selectConversation(lastId);
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- fire once per open after the list arrives; re-running on every list change would fight manual deselection
  }, [show, conversations.length]);

  /** Append a live/sent message, keeping at most THREAD_WINDOW_SIZE rows mounted — older
   *  rows re-page through loadOlderMessages, so hasMore flips back on when the window trims. */
  const appendToThread = (prev: ThreadData | null, message: ChatMessage): ThreadData | null => {
    if (!prev || prev.messages.some((m) => m._id === message._id)) return prev;
    const messages = [...prev.messages, message];
    if (messages.length <= THREAD_WINDOW_SIZE) return { ...prev, messages };
    return { ...prev, messages: messages.slice(messages.length - THREAD_WINDOW_SIZE), hasMore: true };
  };

  const loadOlderMessages = useCallback(async () => {
    const conversationId = selectedIdRef.current;
    if (!conversationId) return;
    const before = threadRef.current?.messages[0]?.createdDate;
    const beforeId = threadRef.current?.messages[0]?._id;
    if (!before) return;
    const response = await getMessagesRequest({
      conversationId: toObjectIdString(conversationId),
      before,
      ...(beforeId ? { beforeId: toObjectIdString(beforeId) } : {}),
    });
    if (response.data.status && selectedIdRef.current === conversationId) {
      const older = response.data.result ?? [];
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: [...older, ...prev.messages.filter((m) => !older.some((o) => o._id === m._id))],
              hasMore: response.data.status ? (response.data.hasMore ?? false) : prev.hasMore,
            }
          : prev,
      );
    }
  }, []);

  /** Chat closed: refresh just the badge, debounced — a busy space chat must not fan out
   *  one full getConversations per message per connected client. */
  const refreshBadgeDebounced = useCallback(() => {
    if (summaryTimerRef.current) return;
    summaryTimerRef.current = setTimeout(() => {
      summaryTimerRef.current = null;
      const epoch = sessionEpochRef.current;
      getUnreadSummaryRequest()
        .then((response) => {
          if (epoch !== sessionEpochRef.current) return;
          if (response.data.status) dispatch(setPortalChatUnreadCount(response.data.unreadConversations ?? 0));
        })
        .catch((error: unknown) => Sentry.captureException(error));
    }, BADGE_REFRESH_DEBOUNCE_MS);
  }, [dispatch]);

  const applyConversationPreview = useCallback(
    (conversationId: string, message: ChatMessage, incrementUnread: boolean) => {
      if (!showRef.current) {
        refreshBadgeDebounced();
        return;
      }
      setConversations((prev) => {
        const known = prev.some((c) => c._id === conversationId);
        if (!known) {
          void refreshList();
          return prev;
        }
        const next = prev.map((c) => {
          if (c._id !== conversationId) return c;
          const mentioned = (message.mentions ?? []).some((m) => String(m) === userId);
          return {
            ...c,
            lastMessageDate: message.createdDate,
            lastMessage: {
              messageId: message._id,
              senderId: message.senderId,
              senderName: message.senderName,
              kind: message.kind,
              text: (message.text ?? "").slice(0, PREVIEW_LENGTH),
              hasAttachments: message.attachments?.length ? true : undefined,
              deleted: message.deleted,
            },
            unreadCount: incrementUnread ? Math.min(c.unreadCount + 1, UNREAD_COUNT_CAP) : c.unreadCount,
            // Resurface parity with the server (PRD §4.11): direct/group return on a new message
            // unless muted (mention bypasses mute); hidden PROJECT chats stay hidden unless mentioned.
            hidden:
              c.hidden &&
              (c.type === "space" ? !mentioned : (c.muted && !mentioned) || String(message.senderId) === userId)
                ? c.hidden
                : false,
          };
        });
        publishBadge(next);
        return next;
      });
    },
    [publishBadge, refreshBadgeDebounced, refreshList, userId],
  );

  const { emitTyping: socketEmitTyping, reconnectNow } = usePortalChatSocket(
    Boolean(userId),
    {
      onConnectionChange: setConnected,
      onMessageNew: ({ conversationId, message }) => {
        const own = String(message.senderId) === userId;
        const isOpen = selectedIdRef.current === conversationId && show;
        applyConversationPreview(conversationId, message, !own && !isOpen);
        if (isOpen) {
          setThread((prev) => appendToThread(prev, message));
          // Only an actually-visible thread counts as read — a background tab/app must not
          // consume read receipts for messages the user never saw. The read catches up via the
          // visibility subscription above once the thread is seen again.
          if (!own) {
            if (platformRef.current.isAppVisible()) markRead(conversationId);
            else pendingReadRef.current = conversationId;
          }
        }
        setTypingByConversation((prev) => {
          const forConversation = { ...(prev[conversationId] ?? {}) };
          delete forConversation[String(message.senderId)];
          return { ...prev, [conversationId]: forConversation };
        });
      },
      onMessageEdited: ({ conversationId, message }) => {
        if (selectedIdRef.current === conversationId) {
          setThread((prev) =>
            prev ? { ...prev, messages: prev.messages.map((m) => (m._id === message._id ? message : m)) } : prev,
          );
        }
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversationId && c.lastMessage?.messageId === message._id
              ? { ...c, lastMessage: { ...c.lastMessage, text: (message.text ?? "").slice(0, PREVIEW_LENGTH) } }
              : c,
          ),
        );
      },
      onMessageDeleted: ({ conversationId, messageId }) => {
        if (selectedIdRef.current === conversationId) {
          setThread((prev) =>
            prev
              ? {
                  ...prev,
                  messages: prev.messages.map((m) =>
                    m._id === messageId
                      ? {
                          ...m,
                          deleted: true,
                          text: undefined,
                          attachments: undefined,
                          reactions: undefined,
                          replyTo: undefined,
                          forwardOf: undefined,
                        }
                      : m,
                  ),
                }
              : prev,
          );
        }
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversationId && c.lastMessage?.messageId === messageId
              ? { ...c, lastMessage: { ...c.lastMessage, text: "", deleted: true } }
              : c,
          ),
        );
      },
      onReactionUpdated: ({ conversationId, messageId, reactions }) => {
        if (selectedIdRef.current !== conversationId) return;
        const branded = reactions.map((reaction) => ({
          emoji: reaction.emoji,
          users: reaction.users.map((id) => toObjectIdString(id)),
        }));
        setThread((prev) =>
          prev
            ? {
                ...prev,
                messages: prev.messages.map((m) => (m._id === messageId ? { ...m, reactions: branded } : m)),
              }
            : prev,
        );
      },
      onReadUpdated: ({ conversationId, userId: readerId, lastReadDate }) => {
        if (selectedIdRef.current !== conversationId) return;
        setThread((prev) =>
          prev
            ? {
                ...prev,
                memberReadDates: [
                  ...prev.memberReadDates.filter((r) => r.userId !== readerId),
                  { userId: readerId, lastReadDate },
                ],
              }
            : prev,
        );
      },
      onConversationUpdated: () => {
        if (!showRef.current) {
          refreshBadgeDebounced();
          return;
        }
        void refreshList();
        const conversationId = selectedIdRef.current;
        if (conversationId) {
          // Membership / name may have changed; refresh the open thread's members lazily.
          void getMessagesRequest({ conversationId: toObjectIdString(conversationId), limit: 1 }).then((response) => {
            if (response.data.status && selectedIdRef.current === conversationId) {
              setThread((prev) =>
                prev ? { ...prev, members: response.data.status ? (response.data.members ?? []) : prev.members } : prev,
              );
            }
          });
        }
      },
      onMemberRemoved: ({ conversationId }) => {
        if (!showRef.current) {
          // The list is empty/stale while closed — let the debounced summary recompute the badge.
          refreshBadgeDebounced();
          return;
        }
        // PRD §4.3: the conversation disappears immediately for the removed member, even if open.
        // Web's two-pane auto-selects the next conversation; mobile deselects instead — selecting
        // would invisibly mark the fallback read while the user is on a different screen.
        setConversations((prev) => {
          const next = prev.filter((c) => c._id !== conversationId);
          publishBadge(next);
          if (selectedIdRef.current === conversationId) {
            const fallback = platformRef.current.autoSelectFallbackOnRemoval ? next.find((c) => !c.hidden) : undefined;
            setTimeout(() => selectConversation(fallback ? fallback._id : null), 0);
          }
          return next;
        });
      },
      onTyping: ({ conversationId, userId: typerId, isTyping }) => {
        setTypingByConversation((prev) => {
          const forConversation = { ...(prev[conversationId] ?? {}) };
          if (isTyping) forConversation[typerId] = { until: Date.now() + TYPING_TTL_MS };
          else delete forConversation[typerId];
          return { ...prev, [conversationId]: forConversation };
        });
      },
    },
    { getConnectOptions: platform.getSocketOptions, reconnectKey: sessionKey },
  );

  // The badge debounce survives renders in a ref; clear it when the host unmounts (web panel
  // never unmounts, but the mobile CoreHost does on logout/portal switch).
  useEffect(
    () => () => {
      if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
    },
    [],
  );

  // Portal switch (mobile): drop every piece of per-portal state in place. The socket hook
  // reconnects on its own via reconnectKey; nothing unmounts, so navigation state survives.
  const prevSessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    if (prevSessionKeyRef.current === sessionKey) return;
    prevSessionKeyRef.current = sessionKey;
    sessionEpochRef.current += 1;
    // A cross-portal push tap navigates to the thread BEFORE the new portal's data resolves and
    // flips sessionKey — that selection is valid in the NEW portal and must survive the reset.
    // A drawer-initiated switch has no chat screen focused (show false), so nothing re-selects.
    const retainedSelection = showRef.current ? selectedIdRef.current : null;
    setConversations([]);
    setSections([]);
    setProjectOrder([]);
    setSelectedId(null);
    setThread(null);
    setComposerIntent(null);
    setHighlightMessageId(null);
    setTypingByConversation({});
    setForwardMessage(null);
    setReceiptsMessage(null);
    setReactionsMessage(null);
    setEditDialogMessage(null);
    setFailedSend(null);
    setUnreadOnly(false);
    setListLoaded(false);
    pendingReadRef.current = null;
    recentlyReadRef.current.clear();
    dispatch(setPortalChatUnreadCount(0));
    if (showRef.current) {
      setLoadingList(true);
      void refreshList().finally(() => setLoadingList(false));
      if (retainedSelection) selectConversation(retainedSelection);
    }
    // prevSessionKeyRef guards re-entry, so extra identity-driven re-runs are no-ops.
  }, [sessionKey, dispatch, refreshList, selectConversation]);

  // Sweep expired typing entries.
  useEffect(() => {
    const timer = setInterval(() => {
      setTypingByConversation((prev) => {
        const nowMs = Date.now();
        let changed = false;
        const next: typeof prev = {};
        for (const [conversationId, users] of Object.entries(prev)) {
          const alive = Object.fromEntries(Object.entries(users).filter(([, v]) => v.until > nowMs));
          if (Object.keys(alive).length !== Object.keys(users).length) changed = true;
          next[conversationId] = alive;
        }
        return changed ? next : prev;
      });
    }, TYPING_SWEEP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const sendMessage = useCallback<PortalChatContextValue["sendMessage"]>(
    async ({ text, attachments, replyToMessageId, forwardOfMessageId, mentions, conversationId: explicitId }) => {
      const conversationId = explicitId ?? selectedIdRef.current;
      if (!conversationId) return false;
      try {
        const response = await sendMessageRequest({
          conversationId: toObjectIdString(conversationId),
          ...(text ? { text } : {}),
          ...(attachments?.length ? { attachments } : {}),
          ...(replyToMessageId ? { replyToMessageId: toObjectIdString(replyToMessageId) } : {}),
          ...(forwardOfMessageId ? { forwardOfMessageId: toObjectIdString(forwardOfMessageId) } : {}),
          ...(mentions?.length ? { mentions: mentions.map((id) => toObjectIdString(id)) } : {}),
        });
        if (!response.data.status) {
          showServerError(response.data.errorMessage, t("Could not send the message"));
          return false;
        }
        const message = response.data.result;
        if (message) {
          // Guarded like every other async path: a reply that lands after the user switched
          // conversations must not append into the newly opened thread.
          if (selectedIdRef.current === conversationId) {
            setThread((prev) => appendToThread(prev, message));
          }
          applyConversationPreview(conversationId, message, false);
        }
        return true;
      } catch (error) {
        Sentry.captureException(error);
        showToast(t("Could not send the message"), false);
        return false;
      }
    },
    [applyConversationPreview, showServerError, showToast, t],
  );

  const editMessage = useCallback(
    async (messageId: string, text: string) => {
      const response = await editMessageRequest({ messageId: toObjectIdString(messageId), text });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Could not edit the message"));
        return false;
      }
      const message = response.data.result;
      if (message) {
        setThread((prev) =>
          prev ? { ...prev, messages: prev.messages.map((m) => (m._id === message._id ? message : m)) } : prev,
        );
      }
      showToast(t("Message edited."));
      return true;
    },
    [showServerError, showToast, t],
  );

  const deleteMessage = useCallback(
    async (messageId: string) => {
      const response = await deleteMessageRequest({ messageId: toObjectIdString(messageId) });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Could not delete the message"));
        return;
      }
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.map((m) =>
                m._id === messageId
                  ? {
                      ...m,
                      deleted: true,
                      text: undefined,
                      attachments: undefined,
                      reactions: undefined,
                      replyTo: undefined,
                      forwardOf: undefined,
                    }
                  : m,
              ),
            }
          : prev,
      );
    },
    [showServerError, t],
  );

  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    const response = await toggleReactionRequest({ messageId: toObjectIdString(messageId), emoji });
    if (response.data.status) {
      const reactions = response.data.reactions ?? [];
      setThread((prev) =>
        prev ? { ...prev, messages: prev.messages.map((m) => (m._id === messageId ? { ...m, reactions } : m)) } : prev,
      );
    }
  }, []);

  const updateConversationState = useCallback(
    async (conversationId: string, patch: { hidden?: boolean; muted?: boolean; sectionId?: string | null }) => {
      const response = await updateConversationStateRequest({
        conversationId: toObjectIdString(conversationId),
        hidden: patch.hidden,
        muted: patch.muted,
        ...(patch.sectionId !== undefined
          ? { sectionId: patch.sectionId === null ? null : toObjectIdString(patch.sectionId) }
          : {}),
      });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Something went wrong"));
        return;
      }
      setConversations((prev) => {
        const next = prev.map((c) =>
          c._id === conversationId
            ? {
                ...c,
                ...(patch.hidden !== undefined ? { hidden: patch.hidden } : {}),
                ...(patch.muted !== undefined ? { muted: patch.muted } : {}),
                ...(patch.sectionId !== undefined
                  ? { sectionId: patch.sectionId === null ? null : toObjectIdString(patch.sectionId) }
                  : {}),
              }
            : c,
        );
        publishBadge(next);
        return next;
      });
    },
    [publishBadge, showServerError, t],
  );

  const deleteAttachment = useCallback(
    async (messageId: string, awsKey: string) => {
      const response = await deleteAttachmentRequest({ messageId: toObjectIdString(messageId), awsKey });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Something went wrong"));
      }
      // The message:edited (or message:deleted) socket echo carries the updated state.
    },
    [showServerError, t],
  );

  const unhideAndOpen = useCallback(
    (conversationId: string) => {
      const conversation = conversations.find((c) => c._id === conversationId);
      if (conversation?.hidden) {
        void updateConversationState(conversationId, { hidden: false });
        showToast(t("{{name}} is back in your list.", { name: conversation.name }));
      }
      selectConversation(conversationId);
    },
    [conversations, selectConversation, showToast, t, updateConversationState],
  );

  const selectNextAfter = useCallback(
    (conversationId: string) => {
      const fallback = conversations.find((c) => c._id !== conversationId && !c.hidden);
      selectConversation(fallback ? fallback._id : null);
    },
    [conversations, selectConversation],
  );

  const jumpToMessage = useCallback(
    (conversationId: string, messageId: string) => {
      // A forward source may have left the viewer's list since the page was rendered
      // (space deleted, project archived, membership lost) — never navigate into a void.
      if (!conversations.some((c) => c._id === conversationId)) {
        showToast(t("This conversation is no longer available"), false);
        return;
      }
      if (selectedIdRef.current !== conversationId) selectConversation(conversationId);
      setHighlightMessageId(messageId);
    },
    [conversations, selectConversation, showToast, t],
  );

  const emitTyping = useCallback(
    (conversationId: string, isTyping: boolean) => socketEmitTyping(conversationId, isTyping),
    [socketEmitTyping],
  );

  // Messages sent while the socket was down were only ever broadcast live — on the
  // disconnected→connected transition, refetch so the thread has no silent hole.
  useEffect(() => {
    const was = prevConnectedRef.current;
    prevConnectedRef.current = connected;
    if (was || !connected) return;
    if (showRef.current) {
      void refreshList();
      if (selectedIdRef.current) selectConversation(selectedIdRef.current);
    } else {
      refreshBadgeDebounced();
    }
  }, [connected, refreshList, selectConversation, refreshBadgeDebounced]);

  const typingForSelected = selectedId ? typingByConversation[selectedId] : undefined;
  const threadMembers = thread?.members;
  const typingUsers = useMemo(() => {
    const users = typingForSelected ?? {};
    const nowMs = Date.now();
    return Object.entries(users)
      .filter(([id, value]) => id !== userId && value.until > nowMs)
      .map(([id]) => ({
        userId: id,
        // Display names come from the trusted member list, never from the socket payload.
        userName: threadMembers?.find((member) => String(member._id) === id)?.fullName ?? t("Someone"),
      }));
  }, [typingForSelected, userId, threadMembers, t]);

  const setScope = useCallback((next: ChatScope) => {
    setScopeState(next);
    platformRef.current.storage.setItem(SCOPE_STORAGE_KEY, next);
  }, []);

  const toggleUnreadOnly = useCallback(() => setUnreadOnly((prev) => !prev), []);

  /** PRD §4.6: shells bump this on layout changes (web: maximise/restore) so open menus close. */
  const bumpMenuEpoch = useCallback(() => setMenuEpoch((prev) => prev + 1), []);

  const contextValue: PortalChatContextValue = {
    userId,
    userName,
    conversations,
    sections,
    projectOrder,
    loadingList,
    listLoaded,
    connected,
    selectedId,
    thread,
    typingUsers,
    composerIntent,
    highlightMessageId,
    currentProjectId,
    scope,
    setScope,
    unreadOnly,
    toggleUnreadOnly,
    selectConversation,
    refreshList,
    loadOlderMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    deleteAttachment,
    toggleReaction,
    setComposerIntent,
    setHighlightMessageId,
    updateConversationState,
    emitTyping,
    markConversationRead: markRead,
    openForwardDialog: setForwardMessage,
    openReceiptsDialog: setReceiptsMessage,
    openReactionsDialog: setReactionsMessage,
    openEditDialog: setEditDialogMessage,
    unhideAndOpen,
    selectNextAfter,
    menuEpoch,
    jumpToMessage,
    failedSend,
    setFailedSend,
    showToast,
    showServerError,
  };

  return {
    contextValue,
    connected,
    /** Dialog-open state lives in the core so both shells drive the same open/close contract. */
    dialogs: {
      forwardMessage,
      receiptsMessage,
      reactionsMessage,
      editDialogMessage,
      closeForward: () => setForwardMessage(null),
      closeReceipts: () => setReceiptsMessage(null),
      closeReactions: () => setReactionsMessage(null),
      closeEdit: () => setEditDialogMessage(null),
    },
    bumpMenuEpoch,
    /** Mobile: nudge a stalled socket on AppState→active / NetInfo regain. */
    reconnectNow,
  };
}
